import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import UpdateUserPermissionsComponent from '@/components/ClientsPage/UpdateUserPermissions/UpdateUserPermissionsComponent';
import { useUser } from '@/contexts/UserContext';
import { useClientUsers } from '@/hooks/useClientUsers';
import { masksByCountry } from '@/helpers/mask';
import { showToast } from '@/store/ducks/toasts';


const UpdateUserPermissionsContainer = () => {
  const [isClientUserDeleteConfirmationVisible, setIsClientUserDeleteConfirmationVisible] = useState(false);
  const [clientUser, setClientUser] = useState(null);
  const [selectedPermissionsIds, setSelectedPermissionsIds] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const { id, status, userId } = useParams();

  const { useUserDetails, useUpdateUserMutation, useDeleteUserMutation } = useClientUsers(id);
  const { data: userData, isLoading: isBusy } = useUserDetails(userId);
  const updateUser = useUpdateUserMutation(userId);
  const deleteUser = useDeleteUserMutation(userId);

  const me = useUser();
  const cpfMask = masksByCountry[me?.profile?.country || 'BR'].CPF_MASK;
  const cpfMaskPlaceholder = masksByCountry[me?.profile?.country || 'BR'].CPF_MASK_PLACEHOLDER;
  const phoneMask = masksByCountry[me?.profile?.country || 'BR'].PHONE_MASK;
  const phoneMaskPlaceholder = masksByCountry[me?.profile?.country || 'BR'].PHONE_MASK_PLACEHOLDER;
  const cellphoneMask = masksByCountry[me?.profile?.country || 'BR'].CELLPHONE_MASK;
  const cellphoneMaskLength = masksByCountry[me?.profile?.country || 'BR'].CELLPHONE_MASK_LENGTH;

  useEffect(() => {
    if (userData) {
      setClientUser({
        username: userData.username,
        email: userData.email,
        name: userData.name,
        cpf: userData.cpf,
        phone: userData.phone,
      });
      
      const initiallySelected = userData?.features
        ?.filter(f => f.selected)
        ?.map(f => f.id) || [];
      setSelectedPermissionsIds(initiallySelected);
    }
  }, [userData]);

  const permissions = useMemo(() => {
    return userData?.features?.map(feature => ({
      id: feature?.id,
      description: intl.formatMessage({ id: `PERMISSION.${feature?.description}` }),
      selected: selectedPermissionsIds.includes(feature?.id),
    })) || [];
  }, [intl, selectedPermissionsIds, userData]);

  const handleSelectedPermissionsChange = useCallback(event => {
    const value = Number(event?.target?.value);
    if (event?.target?.checked) {
      setSelectedPermissionsIds(prev => [...prev, value]);
    } else {
      setSelectedPermissionsIds(prev => prev.filter(id => id !== value));
    }
  }, []);

  const handleClientUserFormSubmit = useCallback(async () => {
    if (!selectedPermissionsIds.length) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'SELECT_AT_LEAST_ONE_PERMISSION_MESSAGE' }),
          duration: 5000,
        })
      );
      return;
    }

    updateUser.mutate({
      ...clientUser,
      features: selectedPermissionsIds,
    }, {
      onSuccess: () => {
        dispatch(
          showToast({
            type: 'success',
            title: intl.formatMessage({ id: 'SUCCESS' }),
            text: intl.formatMessage({ id: 'SAVE_SELECTED_PERMISSIONS_SUCCESS' }),
            duration: 5000,
          })
        );
        navigate(`/clients/${status}/${id}/users/`);
      },
      onError: () => {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'SAVE_SELECTED_PERMISSIONS_ERROR' }),
            duration: 5000,
          })
        );
      }
    });
  }, [clientUser, dispatch, id, intl, navigate, selectedPermissionsIds, status, updateUser]);

  const handleToggleClientUserDeleteConfirmation = useCallback(() => {
    setIsClientUserDeleteConfirmationVisible(prev => !prev);
  }, []);

  const handleClientUserDeleteButtonClick = useCallback(async () => {
    deleteUser.mutate(undefined, {
      onSuccess: () => {
        dispatch(
          showToast({
            type: 'success',
            title: intl.formatMessage({ id: 'SUCCESS' }),
            text: intl.formatMessage({ id: 'USER_DELETE_SUCCESS' }),
            duration: 5000,
          })
        );
        navigate(`/clients/${status}/${id}/users/`);
      },
      onError: () => {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'USER_DELETE_ERROR' }),
            duration: 5000,
          })
        );
      }
    });
  }, [deleteUser, dispatch, id, intl, navigate, status]);

  const onChange = evt => {
    const { name, value } = evt.target;
    setClientUser(prev => ({ ...prev, [name]: value }));
  };

  return (
    <UpdateUserPermissionsComponent
      isBusy={isBusy}
      isClientUserFormSubmitting={updateUser.isPending}
      isClientUserDeleteConfirmationVisible={isClientUserDeleteConfirmationVisible}
      permissions={permissions}
      handleSelectedPermissionsChange={handleSelectedPermissionsChange}
      handleClientUserDeleteButtonClick={handleClientUserDeleteButtonClick}
      handleClientUserFormSubmit={handleClientUserFormSubmit}
      handleToggleClientUserDeleteConfirmation={handleToggleClientUserDeleteConfirmation}
      intl={intl}
      user={clientUser}
      onChange={onChange}
      cpfMask={cpfMask}
      cpfMaskPlaceholder={cpfMaskPlaceholder}
      phoneMask={phoneMask}
      phoneMaskPlaceholder={phoneMaskPlaceholder}
      cellphoneMask={cellphoneMask}
      cellphoneMaskLength={cellphoneMaskLength}
    />
  );
};


export default UpdateUserPermissionsContainer;

